import { useEffect, useRef, useState } from 'react';

import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { createDefaultPreset } from '@atlaskit/editor-core/preset-default';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { alignmentPlugin } from '@atlaskit/editor-plugins/alignment';
import { cardPlugin, type CardPluginOptions } from '@atlaskit/editor-plugins/card';
import { contentInsertionPlugin } from '@atlaskit/editor-plugins/content-insertion';
import { datePlugin } from '@atlaskit/editor-plugins/date';
import { emojiPlugin, type EmojiPluginOptions } from '@atlaskit/editor-plugins/emoji';
import { expandPlugin } from '@atlaskit/editor-plugins/expand';
import { extensionPlugin } from '@atlaskit/editor-plugins/extension';
import { gridPlugin } from '@atlaskit/editor-plugins/grid';
import { highlightPlugin } from '@atlaskit/editor-plugins/highlight';
import { layoutPlugin } from '@atlaskit/editor-plugins/layout';
import { listPlugin } from '@atlaskit/editor-plugins/list';
import { mediaPlugin, type MediaPluginOptions } from '@atlaskit/editor-plugins/media';
import { mentionsPlugin, type MentionsPluginOptions } from '@atlaskit/editor-plugins/mentions';
import { panelPlugin } from '@atlaskit/editor-plugins/panel';
import { rulePlugin } from '@atlaskit/editor-plugins/rule';
import { selectionMarkerPlugin } from '@atlaskit/editor-plugins/selection-marker';
import { showDiffPlugin } from '@atlaskit/editor-plugins/show-diff';
import { statusPlugin } from '@atlaskit/editor-plugins/status';
import { syncedBlockPlugin } from '@atlaskit/editor-plugins/synced-block';
import { tablePlugin } from '@atlaskit/editor-plugins/table';
import { tasksAndDecisionsPlugin } from '@atlaskit/editor-plugins/tasks-and-decisions';
import { textColorPlugin } from '@atlaskit/editor-plugins/text-color';
import { trackChangesPlugin } from '@atlaskit/editor-plugins/track-changes';
import { aiPlugin } from '@atlassian/editor-plugin-ai';
import { aiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import { createPageEditorPluginAIOptions } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';

/**
 * Returns a full page preset built with preview props and the preset's editor API.
 * For use in EditorPreview and other contexts that need a minimal Confluence full page editor.
 * Includes every @atlaskit/editor-plugins plugin that provides nodeViews,
 * plus mark plugins (text formatting, text color, highlight, selection marker; clearMarksOnEmptyDoc is in the default preset).
 */
export function usePreviewFullPagePreset({
	providerFactory,
	isOriginalPreview,
}: {
	isOriginalPreview?: boolean;
	providerFactory?: ProviderFactory;
}): ReturnType<typeof usePreset> {
	const emojiProvider = useRef<EmojiPluginOptions['emojiProvider']>();
	const mentionProvider = useRef<MentionsPluginOptions['mentionProvider']>();
	const mediaProvider = useRef<MediaPluginOptions['provider']>();
	const cardProvider = useRef<CardPluginOptions['provider']>();
	const [isProviderReady, setIsProviderReady] = useState(false);
	useEffect(() => {
		emojiProvider.current = providerFactory?.subscribe('emojiProvider', (_, provider) => provider);
		mentionProvider.current = providerFactory?.subscribe(
			'mentionProvider',
			(_, provider) => provider,
		);
		mediaProvider.current = providerFactory?.subscribe('mediaProvider', (_, provider) => provider);
		cardProvider.current = providerFactory?.subscribe('cardProvider', (_, provider) => provider);
		// const extensionProvider = providerFactory?.subscribe(
		// 	'extensionProvider',
		// 	(_, provider) => provider,
		// );

		setIsProviderReady(true);
	}, [providerFactory]);

	return usePreset(
		() =>
			createDefaultPreset({ appearance: 'full-page' })
				.add(gridPlugin)
				.add(alignmentPlugin)
				.add([
					emojiPlugin,
					{
						emojiProvider: emojiProvider.current,
					},
				])
				.add(contentInsertionPlugin)
				.add([
					cardPlugin,
					{
						provider: cardProvider.current,
						allowBlockCards: true,
						allowEmbeds: true,
						allowResizing: true,
					},
				])
				.add(datePlugin)
				.add(expandPlugin)
				.add(extensionPlugin)
				.add(layoutPlugin)
				.add(listPlugin)
				.add([
					mediaPlugin,
					{
						allowCaptions: true,
						allowMediaSingle: true,
						allowLinking: true,
						allowMediaInlineImages: true,
						provider: mediaProvider.current,
					},
				])
				.add([
					mentionsPlugin,
					{
						mentionProvider: mentionProvider.current,
					},
				])
				.add(panelPlugin)
				.add(rulePlugin)
				.add(selectionMarkerPlugin)
				.add(statusPlugin)
				.add(syncedBlockPlugin)
				.add(tablePlugin)
				.add(tasksAndDecisionsPlugin)
				.add(textColorPlugin)
				.add(highlightPlugin)
				/**
				 * Plugins needed for showing suggestions
				 */
				.maybeAdd(showDiffPlugin, !isOriginalPreview)
				.maybeAdd(trackChangesPlugin, !isOriginalPreview)
				.maybeAdd(
					[
						aiPlugin,
						createPageEditorPluginAIOptions({
							product: 'CONFLUENCE',
							aiGlobalOptIn: { status: 'enabled', triggerOptInFlow: () => {} },
							objectId: 'editor-preview',
							PromptEditor: (_props): JSX.Element => null as unknown as JSX.Element,
						}),
					],
					!isOriginalPreview,
				)
				.maybeAdd(
					[
						aiStreamingOrchestratorPlugin,
						{
							streamFlowPacerEnabled: false,
						},
					],
					!isOriginalPreview,
				),
		[isProviderReady],
	);
}
